import { msg } from "@lingui/core/macro";
import { createEffect, Match, Switch } from "solid-js";
import { createRoute, useNavigate, useParams } from "@tanstack/solid-router";
import { appRoute } from "./route";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import { createAccountPowerSyncSingle } from "@/lib/powersync/create-account-query";
import { getTodoDetailQuery, queryKeys } from "@listto/shared";
import { FullPageLoader } from "@/components/full-page-loader";
import { NotFoundState } from "@/components/ui/not-found-state";
import { PageContainer } from "@fleet/ui";
import { createEntityResolution } from "@/lib/use-entity-resolution";
import { useI18n } from "@/providers/i18n-provider";

/**
 * Permission-aware deep-link to the detail of a task by its global id (`/todo/$id`).
 *
 * This is a **shareable** URL: someone pastes it without knowing in which smart-view or
 * list the task lives. We resolve the task respecting permissions/visibility (PowerSync's
 * sync rules do not download rows without access ⇒ absent = no access) and:
 *   - present → redirects to its canonical URL (list or inbox) so the detail
 *     drawer opens in its real context;
 *   - not found / without access → `NotFoundState` (does not bounce home in
 *     silence: gives clear feedback with an action to go back);
 *   - offline and not yet synced → offline state, NEVER a hung loader.
 */
function LegacyTodoRedirectPage() {
  const { t } = useI18n();
  const params = useParams({ from: "/_app/todo/$todoId" });
  const navigate = useNavigate();
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();

  const todoQuery = createAccountPowerSyncSingle({
    queryKey: () => queryKeys.todos.detailRedirect(params().todoId),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      return dbInstance ? getTodoDetailQuery(dbInstance, params().todoId) : null;
    },
    enabled: () => Boolean(params().todoId) && Boolean(drizzle()),
  });

  // Resolves the task: present / loading (sync) / not-found / offline.
  // Reuses campaign #1: avoids leaving the screen loading forever when the
  // task does not exist, was deleted or is not accessible for this user.
  const resolution = createEntityResolution({
    db: () => db(),
    hasEntity: () => Boolean(todoQuery.data),
    isPending: () => todoQuery.isLoading,
  });

  // When the task resolves to "ready", we jump to its canonical URL (in its list or
  // in inbox) to open the detail drawer in its real context.
  createEffect(() => {
    if (resolution() !== "ready") {
      return;
    }
    const todo = todoQuery.data;
    if (!todo) {
      return;
    }
    if (todo.list_id) {
      void navigate({
        to: "/lists/$listId/todo/$todoId",
        params: { listId: todo.list_id, todoId: todo.id },
        replace: true,
      });
    } else {
      void navigate({
        to: "/inbox/todo/$todoId",
        params: { todoId: todo.id },
        replace: true,
      });
    }
  });

  return (
    <Switch fallback={<FullPageLoader />}>
      <Match when={resolution() === "not-found"}>
        <PageContainer maxWidth="2xl" class="py-16">
          <NotFoundState
            variant="not-found"
            entityLabel={t(msg`task`)}
            offerAccountSwitch
            primaryAction={{
              label: t(msg`Back to home`),
              onClick: () => {
                void navigate({ to: "/", replace: true });
              },
            }}
          />
        </PageContainer>
      </Match>
      <Match when={resolution() === "offline"}>
        <PageContainer maxWidth="2xl" class="py-16">
          <NotFoundState
            variant="offline"
            entityLabel={t(msg`task`)}
            primaryAction={{
              label: t(msg`Back to home`),
              onClick: () => {
                void navigate({ to: "/", replace: true });
              },
            }}
          />
        </PageContainer>
      </Match>
    </Switch>
  );
}

export const globalTodoRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/todo/$todoId",
  component: LegacyTodoRedirectPage,
});
